import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { IApiRecurso, IApiToken, useApiController } from '../../../hooks/useApiController';
import { IDataNoPagination } from '../../../types/shared';

interface Props {
  token: IApiToken | null;
  onClose: () => void;
}

// Ver config do token + editar nome, COLUNAS (allowlist) e status (suspender/reativar).
// Empresa, cliente e tabelas ficam READ-ONLY (mudar = criar novo token).
export function TokenDetalhesModal({ token, onClose }: Props) {
  const queryClient = useQueryClient();
  const { getRecursos, updateToken } = useApiController();

  const [nome, setNome] = useState('');
  const [suspenso, setSuspenso] = useState(false);
  const [colunasPorTabela, setColunasPorTabela] = useState<Record<string, string[]>>({});

  // precisa do recurso do token p/ saber as tabelas/colunas disponíveis
  const { data: recursosData } = useQuery<IDataNoPagination<IApiRecurso[]>>({
    queryKey: ['api-controller-recursos'],
    queryFn: getRecursos,
    enabled: !!token,
  });
  const recurso = useMemo(
    () => (recursosData?.data ?? []).find(r => r.id === token?.api_recurso_id),
    [recursosData, token],
  );

  // empresas do token (multi-empresa); fallback p/ o external_id singular legado
  const empresaIds = useMemo(() => {
    if (token?.external_ids?.length) return token.external_ids;
    return token?.external_id != null ? [token.external_id] : [];
  }, [token]);

  useEffect(() => {
    if (token) {
      setNome(token.nome);
      setSuspenso(token.status === 'lockout');
      setColunasPorTabela({ ...token.colunas_por_tabela });
    }
  }, [token]);

  const toggleColuna = (tabela: string, coluna: string) => {
    setColunasPorTabela(prev => {
      const atual = prev[tabela] ?? [];
      const novo = atual.includes(coluna) ? atual.filter(c => c !== coluna) : [...atual, coluna];
      return { ...prev, [tabela]: novo };
    });
  };

  const toggleTodasColunas = (tabela: string, colunas: string[], marcarTodas: boolean) => {
    setColunasPorTabela(prev => ({ ...prev, [tabela]: marcarTodas ? [...colunas] : [] }));
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-controller-tokens'] });
      toast.success('Token atualizado', { position: 'top-right' });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail ?? 'Erro ao atualizar token', {
        position: 'top-right',
      });
    },
  });

  const handleSalvar = async () => {
    if (!token) return;
    // revogado é kill definitivo — não reativa por aqui (só ativo <-> suspenso/lockout)
    const status = suspenso ? 'lockout' : 'ativo';
    const limpo: Record<string, string[]> = {};
    Object.entries(colunasPorTabela).forEach(([t, cols]) => {
      if (cols.length > 0) limpo[t] = cols;
    });
    await mutateAsync({
      id: token.id,
      data: { nome: nome.trim(), status, colunas_por_tabela: limpo },
    });
  };

  const tabelas = recurso?.tabelas ?? [];
  const revogado = token?.status === 'revogado';

  return (
    <Modal isOpen={!!token} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Configurações do token</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display="flex" flexDirection="column" gap={4}>
            {revogado && (
              <Box bg="red.50" p={2} borderRadius="md">
                <Text fontSize="sm" color="red.700">
                  Token revogado — não pode ser reativado. Crie um novo.
                </Text>
              </Box>
            )}

            <SimpleGrid columns={2} spacing={3}>
              <FormControl>
                <FormLabel>Empresas</FormLabel>
                <Flex gap={1} flexWrap="wrap">
                  {empresaIds.map(id => (
                    <Badge key={id} colorScheme="blue">
                      Empresa {id}
                    </Badge>
                  ))}
                </Flex>
              </FormControl>
              <FormControl>
                <FormLabel>Cliente</FormLabel>
                <Text fontSize="sm">{token?.usuario_nome ?? '—'}</Text>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel>Nome</FormLabel>
              <Input value={nome} onChange={e => setNome(e.target.value)} />
            </FormControl>

            {!revogado && (
              <FormControl display="flex" alignItems="center" gap={3}>
                <Switch isChecked={suspenso} onChange={e => setSuspenso(e.target.checked)} />
                <Box>
                  <FormLabel mb={0}>{suspenso ? 'Suspenso' : 'Ativo'}</FormLabel>
                  <Text fontSize="xs" color="gray.500">
                    Suspender bloqueia o token temporariamente (reversível).
                  </Text>
                </Box>
              </FormControl>
            )}

            {/* Colunas: EDITÁVEIS por tabela (allowlist). Tabelas read-only. */}
            <FormControl>
              <FormLabel>Colunas liberadas por tabela</FormLabel>
              <Box display="flex" flexDirection="column" gap={3}>
                {tabelas.map(t => {
                  const selecionadas = colunasPorTabela[t.tabela_fonte] ?? [];
                  const todasSelecionadas =
                    t.colunas.length > 0 && selecionadas.length === t.colunas.length;
                  const algumaSelecionada = selecionadas.length > 0;
                  return (
                    <Box key={t.tabela_fonte} borderWidth="1px" borderRadius="md" p={2}>
                      <Flex align="center" justify="space-between" mb={1}>
                        <Text fontSize="sm" fontWeight="600">
                          {t.tabela_fonte}
                        </Text>
                        <Checkbox
                          size="sm"
                          isChecked={todasSelecionadas}
                          isIndeterminate={algumaSelecionada && !todasSelecionadas}
                          onChange={() =>
                            toggleTodasColunas(t.tabela_fonte, t.colunas, !todasSelecionadas)
                          }
                        >
                          <Text fontSize="xs" color="gray.500">
                            Selecionar tudo
                          </Text>
                        </Checkbox>
                      </Flex>
                      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={1}>
                        {t.colunas.map(col => (
                          <Checkbox
                            key={col}
                            size="sm"
                            isChecked={selecionadas.includes(col)}
                            onChange={() => toggleColuna(t.tabela_fonte, col)}
                          >
                            {col}
                          </Checkbox>
                        ))}
                      </SimpleGrid>
                    </Box>
                  );
                })}
              </Box>
            </FormControl>
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="outline" onClick={onClose} isDisabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSalvar}
            isLoading={isPending}
            isDisabled={revogado || nome.trim().length < 2}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
